import {ChangeEvent, useCallback, useEffect, useRef, useState} from 'react';
import Lottie from 'react-lottie';

import {WOFSlice} from '@src/Components/SharedConfigProvider';
import {
	ESharedEvents,
	useSharedEvent,
} from '@src/Components/SharedEventProvider';
import logo from 'url:../../public/LIMODOR_Logo.svg';

import * as classes from './Quiz.module.scss';
import {QuizQuestion, randomQuestionForType} from './QuizQuestions';
import WOFComponent from './WOFComponent';
import animationFail from '../../public/animations/fail.json';
import animationSuccess from '../../public/animations/success.json';

enum QuizPages {
	Start,
	Quiz,
	Fail,
	Success,
}

const failDuration = 5000;
const successDuration = 6000;

const animationSuccessOption = {
	loop: true,
	autoplay: true,
	animationData: animationSuccess,
};

const animationFailOption = {
	loop: true,
	autoplay: true,
	animationData: animationFail,
};

function Quiz() {
	const {sendEvent} = useSharedEvent();

	const [isSpinning, setIsSpinning] = useState(false);
	const [selectedAnswer, setSelectedAnswer] = useState<string | undefined>(
		undefined
	);
	const [currentPage, setCurrentPage] = useState(QuizPages.Start);
	const [currentQuestion, setCurrentQuestion] = useState<
		QuizQuestion | undefined
	>(undefined);

	const redirectTimeout = useRef<number | null>(null);

	// Answer State Management
	const handleRadioChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			setSelectedAnswer(event.target.value);
		},
		[]
	);

	// Current Page State Manegement
	const showFailedScreen = useCallback(() => {
		setCurrentPage(QuizPages.Fail);
	}, []);

	const showSuccessScreen = useCallback(() => {
		// Inform video player about win
		sendEvent(ESharedEvents.QuizWinEvent);

		setCurrentPage(QuizPages.Success);
	}, [sendEvent]);

	// Handle Answer Confirmed Action
	const handleAnswerConfirmed = useCallback(() => {
		if (
			currentQuestion &&
			selectedAnswer === currentQuestion.correctAnswerId
		) {
			showSuccessScreen();
		} else {
			showFailedScreen();
		}
	}, [currentQuestion, selectedAnswer, showFailedScreen, showSuccessScreen]);

	// Start/Stop Spinning Wheel
	const handleSpinningStartStop = useCallback(() => {
		setIsSpinning((v) => !v);
	}, []);

	const wofSpinningWin = useCallback(
		(wofSlice: WOFSlice) => {
			if (wofSlice === WOFSlice.NoPrize) {
				showFailedScreen();
			} else {
				const question = randomQuestionForType(wofSlice);
				setCurrentQuestion(question);
				setSelectedAnswer(undefined);
				setCurrentPage(QuizPages.Quiz);
			}
		},
		[showFailedScreen]
	);

	// Handle Redirect after timeout
	const displayStartPage = useCallback(() => {
		if (
			currentPage === QuizPages.Fail ||
			currentPage === QuizPages.Success
		) {
			setCurrentPage(QuizPages.Start);
		}
		redirectTimeout.current = null;
	}, [currentPage]);
	useEffect(() => {
		if (redirectTimeout.current === null) {
			if (currentPage === QuizPages.Fail) {
				redirectTimeout.current = window.setTimeout(
					displayStartPage,
					failDuration
				);
			} else if (currentPage === QuizPages.Success) {
				redirectTimeout.current = window.setTimeout(
					displayStartPage,
					successDuration
				);
			}
		}

		return () => {
			if (redirectTimeout.current) {
				window.clearTimeout(redirectTimeout.current);
				redirectTimeout.current = null;
			}
		};
	}, [currentPage, displayStartPage]);

	return (
		<>
			{(currentPage === QuizPages.Start ||
				currentPage === QuizPages.Quiz) && (
				<div className={classes.quizContainer}>
					<div className={classes.spinnerContainer}>
						<WOFComponent
							isSpinning={isSpinning}
							onWin={wofSpinningWin}
						/>
					</div>
					<div className={classes.content}>
						<div>
							<img className={classes.logo} src={logo} />
						</div>
						<div className={classes.textContainer}>
							{currentPage === QuizPages.Start && (
								<button
									className={classes.startButton}
									onClick={handleSpinningStartStop}
								>
									{isSpinning ? 'Stop' : 'Start'}
								</button>
							)}
							{currentPage === QuizPages.Quiz &&
								currentQuestion && (
									<>
										<h1>{currentQuestion.question}</h1>
										{currentQuestion.answers.map(
											(answer) => (
												<div key={answer.id}>
													<input
														id={answer.id}
														type='radio'
														name='quizQuestion'
														value={answer.id}
														checked={
															selectedAnswer ===
															answer.id
														}
														onChange={
															handleRadioChange
														}
													/>
													<label htmlFor={answer.id}>
														{answer.value}
													</label>
												</div>
											)
										)}
										<button
											type='submit'
											onClick={handleAnswerConfirmed}
										>
											Weiter
										</button>
									</>
								)}
						</div>
					</div>
				</div>
			)}
			{/* Success Screen */}
			{currentPage === QuizPages.Success && (
				<div className={classes.messageContainer}>
					<Lottie
						options={animationSuccessOption}
						height={1000}
						width={1000}
					/>
					<div className={classes.messageContent}>
						<h1 className={classes.messageTitle}>Glückwunsch!</h1>
						<p>
							Das war die richtige Antwort. Viel Spaß mit dem
							Gewinn!
						</p>
					</div>
				</div>
			)}
			{/* Failed Screen */}
			{currentPage === QuizPages.Fail && (
				<div className={classes.messageContainer}>
					<Lottie
						options={animationFailOption}
						height={1000}
						width={1000}
					/>
					<div className={classes.messageContent}>
						<h1 className={classes.messageTitle}>Oh nein!</h1>
					</div>
				</div>
			)}
		</>
	);
}

export default Quiz;
